/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.mainPriceContainer = this.section.querySelector('.price-container');
        this.syncPrice();

        // Prikaži sticky ATC kad god glavno "Dodaj u korpu" dugme NIJE u vidokrugu —
        // uključujući i na vrhu strane, pre nego što se dugme uopšte vidi (kao na fajlu).
        // Koristimo i scroll listener i IntersectionObserver zajedno radi pouzdanosti.
        this.classList.remove('invisible');

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll, { passive: true });
        StickyAtc.handleScroll.call(this);
        requestAnimationFrame(() => StickyAtc.handleScroll.call(this));
        window.addEventListener('load', () => StickyAtc.handleScroll.call(this));

        if ('IntersectionObserver' in window && this.buyButtons) {
          const topOffset = document.querySelector('.pageheader--sticky')
            ? document.querySelector('.section-header').offsetHeight
            : 0;
          this.io = new IntersectionObserver(
            (entries) => {
              this.classList.remove('invisible');
              this.classList.toggle('sticky-atc--out', entries[0].isIntersecting);
            },
            { rootMargin: `-${topOffset}px 0px 0px 0px`, threshold: 0 }
          );
          this.io.observe(this.buyButtons);
        }

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        if (this.throttledOnScroll) window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        const rect = this.buyButtons.getBoundingClientRect();
        const inView = rect.bottom > topOffset && rect.top < window.innerHeight;
        this.classList.remove('invisible');
        this.classList.toggle('sticky-atc--out', inView);
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          if (this.variantTitle) this.variantTitle.textContent = evt.detail.variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
        // Osveži cenu u sticky baru (posle što tema ažurira glavnu cenu)
        requestAnimationFrame(() => this.syncPrice());
      }

      /**
       * Preslikava trenutnu cenu proizvoda u sticky bar.
       */
      syncPrice() {
        if (!this.priceEl) return;
        const container = this.mainPriceContainer || this.section.querySelector('.price-container');
        if (!container) return;
        const cur = container.querySelector('.price__current');
        if (cur) this.priceEl.textContent = cur.textContent.trim();
        this.priceEl.classList.toggle('sticky-atc__price--sale', !!container.querySelector('.price--on-sale'));
      }

      /**
       * Updates the image.
       */
      updateImage() {
        if (!this.imageContainer) return;
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
